// Broker portal access card on the loan Parties tab — the single broker
// portal account that can see this loan's broker surfaces, and the manual
// (re-)link affordance. Unlike Borrower/Seller (lists), the broker resolves
// to ONE account. Mirrors BorrowerPortalAccessCard. Reads self-gate on
// Vendors.Read; linking gated Vendors.Manage / ADMIN. The picker searches the
// existing /portal/admin/accounts registry filtered to Broker accounts.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import { Button, Card, StatusBadge } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { portalAccountsApi } from "@/api/portalAccounts";
import {
  partyLinkPickerKeys,
  useLinkBroker,
  useLoanBrokerLink,
} from "@/hooks/portfolioAdmin/useVendorReports";

export function BrokerPortalAccessCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const canRead = auth.hasPermission("Vendors.Read");
  const canManage =
    auth.hasPermission("Vendors.Manage") || auth.hasRoleCode("ADMIN");
  const linkQuery = useLoanBrokerLink(loanId, { enabled: canRead });
  const [linkOpen, setLinkOpen] = useState(false);

  if (!canRead) return null;

  const data = linkQuery.data ?? null;
  const linked = data && data.portalUserId ? data : null;

  return (
    <Card data-testid="broker-portal-access-card">
      <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-2">
        <h3 className="font-display font-bold text-h3 m-0">
          Broker portal access
        </h3>
        {canManage && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLinkOpen(true)}
            data-testid="broker-link-button"
          >
            {linked ? (
              <>
                <Pencil size={14} aria-hidden /> Change / re-link
              </>
            ) : (
              <>
                <Plus size={14} aria-hidden /> Link broker portal account
              </>
            )}
          </Button>
        )}
      </div>
      <p className="text-caption text-gray-mid m-0 mb-space-2">
        The linked account sees this loan&apos;s broker surfaces in the broker
        portal.
      </p>

      {linkQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">
          Loading linked account…
        </p>
      )}
      {linkQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load the linked portal account. Refresh to retry.
        </p>
      )}
      {linkQuery.isSuccess &&
        (linked ? (
          <div
            className="flex items-center justify-between gap-space-2 border-b border-border-light pb-space-1 text-small"
            data-testid={`broker-link-row-${linked.portalUserId}`}
          >
            <span className="flex items-center gap-space-2 min-w-0">
              <span className="font-medium whitespace-nowrap">
                {linked.fullName ?? "Unnamed account"}
              </span>
              {linked.email && (
                <span className="text-gray-mid truncate">{linked.email}</span>
              )}
              <StatusBadge tone="neutral">linked</StatusBadge>
            </span>
          </div>
        ) : (
          <p className="text-small text-gray-mid m-0">
            No broker portal account is linked to this loan yet.
          </p>
        ))}

      {linkOpen && (
        <LinkBrokerModal
          loanId={loanId}
          open={linkOpen}
          onClose={() => setLinkOpen(false)}
        />
      )}
    </Card>
  );
}

function LinkBrokerModal({
  loanId,
  open,
  onClose,
}: {
  loanId: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [portalUserId, setPortalUserId] = useState("");
  const link = useLinkBroker(loanId);

  const accountsQuery = useQuery({
    queryKey: partyLinkPickerKeys.search("Broker", search),
    queryFn: () =>
      portalAccountsApi.list({ accountType: "Broker", search, limit: 20 }),
  });
  const accounts = accountsQuery.data?.items ?? [];

  const onSubmit = async () => {
    if (!portalUserId) {
      toast.error("Pick a portal account from the list.");
      return;
    }
    try {
      await link.mutateAsync(portalUserId);
      toast.success("Broker portal account linked.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Link broker portal account"
      description="Pick the broker's existing portal account. This resolves the single broker account for this loan's surfaces."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={!portalUserId || link.isPending}
            data-testid="broker-link-submit"
          >
            Link account
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="broker-link-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Find portal account
          </span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="broker-link-search"
          />
        </label>

        <div
          className="max-h-48 overflow-y-auto border border-border-light rounded-md"
          data-testid="broker-link-list"
        >
          {accountsQuery.isLoading && (
            <p className="text-caption text-gray-mid italic p-space-2 m-0">
              Loading accounts…
            </p>
          )}
          {accountsQuery.isError && (
            <p className="text-caption text-status-error p-space-2 m-0">
              Could not load portal accounts.
            </p>
          )}
          {accountsQuery.isSuccess && accounts.length === 0 && (
            <p className="text-caption text-gray-mid italic p-space-2 m-0">
              No portal accounts match.
            </p>
          )}
          {accounts.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setPortalUserId(a.id)}
              className={`flex w-full items-center justify-between gap-space-2 px-space-2 py-space-1 text-left hover:bg-white-warm transition-colors ${
                portalUserId === a.id ? "bg-gold/10" : ""
              }`}
              data-testid={`broker-link-option-${a.id}`}
            >
              <span className="text-small min-w-0 truncate">
                {a.fullName}
                <span className="text-gray-mid"> · {a.email}</span>
              </span>
              <span className="text-caption text-gray-mid whitespace-nowrap">
                {a.accountType}
              </span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
